import React, { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RecoilRoot } from "recoil";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { theme } from "styles/theme";
import {
  Experimental_CssVarsProvider as MaterialCssVarsProvider,
  THEME_ID as MATERIAL_THEME_ID,
} from "@mui/material/styles";
import { CssVarsProvider as JoyCssVarsProvider } from "@mui/joy/styles";
import { GlobalStyles } from "@mui/joy";
import { materialTheme } from "styles/materialTheme";
import "material-symbols";
import mixpanel from "mixpanel-browser";
import { GOOGLE_CLIENTID, isDev } from "config";
import useMixpanelWeb from "hooks/useMixpanelWeb";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { initializeApp } from "firebase/app";
import { SendbirdWrapper } from "./sendbird/SendbirdWrapper";

// 라우팅에 필요한 모든 컴포넌트와 설정을 import 합니다.
import RootLayout from "./routes/RootLayout";
import { PATH } from "./routes/paths";
import Login from "pages/user/Login";
import Home from "pages/home/Home";
import KakaoLoginRedirect from "pages/user/KakaoLoginRedirect";
import Survey from "pages/Survey";
import TossPaymentsFail from "pages/tosspayments/TossPaymentsFail";
import TossPaymentsSuccess from "pages/tosspayments/TossPaymentsSuccess";
import TossPayments from "pages/tosspayments/TossPayments";
import AppleLoginRedirect from "pages/user/AppleLoginRedirect";
import SelfCheck from "pages/self-check/SelfCheck";
import SelfCheckEnd from "pages/self-check/SelfCheckEnd";
import EmailAuthResultPage from "pages/user/EmailAuthResultPage";
import EmailAuthPage from "pages/user/EmailAuthPage";
import InstitutionSelectPage from "pages/user/InstitutionSelectPage";
import MeditationTool from "pages/tool/meditation/MeditationTool";
import NewsletterSettings from "pages/newsletter/NewsletterSettings";
import OnboardingScreen from "pages/onboarding/OnboardingScreen";
import EtcWhere from "pages/onboarding/optional/EtcWhere";
import AudioGuide from "pages/tool/guide/AudioGuide";
import PaymentContent3Month295 from "components/task/payment/PaymentContent3Month295";
import PromotionBook from "pages/promotion/PromotionBook";
import PromotionBookPhoneNumberSubmit from "pages/promotion/PromotionBookPhoneNumberSubmit";

declare global {
  interface Window {
    Kakao: any;
    ReactNativeWebView: {
      postMessage(msg: string): void;
    };
  }

  interface Array<T> {
    findLastIndex(
      predicate: (value: T, index: number, obj: T[]) => unknown,
      thisArg?: any
    ): number;
  }
}

export const firebaseConfig_dev = {
  apiKey: "AIzaSyAX_O-NntYzO4Bmh8M8G7PUpcP8cIwW8XI",
  authDomain: "distancing-dev.firebaseapp.com",
  projectId: "distancing-dev",
  storageBucket: "distancing-dev.appspot.com",
  messagingSenderId: "193897208164",
  appId: "1:193897208164:web:6c796023f17e225257c271",
  measurementId: "G-TXJBESPEG7",
};
export const firebaseConfig_real = {
  apiKey: "AIzaSyBMCcsN-xmAlgj6wnA1qW-S9Xlbuzdk4OI",
  authDomain: "distancing-404509.firebaseapp.com",
  projectId: "distancing-404509",
  storageBucket: "distancing-404509.appspot.com",
  messagingSenderId: "938509636319",
  appId: "1:938509636319:web:5be2e679961d04947e4f6a",
  measurementId: "G-DMCFDDT4V3",
};

const firebaseConfig = isDev ? firebaseConfig_dev : firebaseConfig_real;

// createBrowserRouter를 사용하여 라우트 설정을 객체 형태로 정의합니다.
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: PATH.login, element: <Login /> },
      { path: PATH.onboarding_new, element: <OnboardingScreen /> },
      { path: PATH.etc_where, element: <EtcWhere /> },
      { path: PATH.home, element: <Home /> },
      { path: PATH.redirect, element: <KakaoLoginRedirect /> },
      { path: PATH.redirect_apple, element: <AppleLoginRedirect /> },
      { path: PATH.survey, element: <Survey /> },
      { path: PATH.paymentV2, element: <PaymentContent3Month295 /> },
      { path: PATH.tossPayments, element: <TossPayments /> },
      { path: PATH.tossPaymentsSuccess, element: <TossPaymentsSuccess /> },
      { path: PATH.tossPaymentsFail, element: <TossPaymentsFail /> },
      { path: PATH.newsletterSettings, element: <NewsletterSettings /> },
      { path: PATH.selfCheck, element: <SelfCheck /> },
      { path: PATH.selfCheckEnd, element: <SelfCheckEnd /> },
      { path: PATH.institutionSelect, element: <InstitutionSelectPage /> },
      { path: PATH.emailAuth, element: <EmailAuthPage /> },
      { path: PATH.emailAuthResult, element: <EmailAuthResultPage /> },
      { path: PATH.meditationTool, element: <MeditationTool /> },
      { path: PATH.guide, element: <AudioGuide /> },
      { path: PATH.promotion, element: <PromotionBook /> },
      {
        path: PATH.promotionPhoneNumberSubmit,
        element: <PromotionBookPhoneNumberSubmit />,
      },
    ],
  },
]);

function App() {
  const [queryClient] = useState(() => new QueryClient());
  const body = document.body;
  body.style.position = "fixed";
  body.style.overflow = "hidden";
  const isHome = window.location.pathname === "/";

  const setMixPanel_come = useMixpanelWeb({
    eventName: "진입",
  });

  useEffect(() => {
    if (isHome) {
      setMixPanel_come();
    }
  }, [isHome, setMixPanel_come]);

  const projectId = isDev
    ? "306a1663b1fece6e28324733a9aca2ee"
    : "6bb06d65a8dec1d9dec0496adef7c8d0";

  const googleClientid = GOOGLE_CLIENTID;

  const app = initializeApp(firebaseConfig);

  mixpanel.init(projectId, { debug: true });
  mixpanel.set_config({
    api_method: "POST",
    api_transport: "XHR",
    batch_requests: false,
    batch_size: 50,
    batch_flush_interval_ms: 5000,
    batch_request_timeout_ms: 90000,
    cookie_domain: "",
    cookie_expiration: 365,
    cross_site_cookie: false,
    cross_subdomain_cookie: true,
    debug: false,
    disable_persistence: false,
    ip: true,
    opt_out_tracking_by_default: false,
    opt_out_persistence_by_default: false,
    opt_out_tracking_persistence_type: "localStorage",
    opt_out_tracking_cookie_prefix: undefined,
    persistence: "cookie",
    persistence_name: "",
    property_blacklist: [],
    secure_cookie: false,
    track_links_timeout: 300,
    upgrade: false,
    xhr_headers: {},
    ignore_dnt: false,
  });

  return (
    <GoogleOAuthProvider clientId={googleClientid}>
      <MaterialCssVarsProvider
        defaultMode="system"
        theme={{ [MATERIAL_THEME_ID]: materialTheme }}
      >
        <JoyCssVarsProvider defaultMode="system" theme={theme}>
          <GlobalStyles
            styles={{
              html: {
                boxSizing: "border-box",
              },
              body: {},
            }}
          />
          <RecoilRoot>
            <QueryClientProvider client={queryClient}>
              <SendbirdWrapper>
                <RouterProvider router={router} />
              </SendbirdWrapper>
            </QueryClientProvider>
          </RecoilRoot>
        </JoyCssVarsProvider>
      </MaterialCssVarsProvider>
    </GoogleOAuthProvider>
  );
}

export default App;